import type { User } from 'firebase/auth';
import { Copy, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { TabNavigation, type TabConfig } from '../../components/TabNavigation';
import { UserIdSearch } from '../../components/UserIdSearch';
import { UserLinks } from '../../components/UserLinks';
import { useAuth } from '../../hooks/useAuth';
import type { ObsessLessUser } from '../../types/commonTypes';
import { DisplayOcdInfo } from './components/DisplayOcdInfo';
import { GenerateOcdProfileModal } from './components/GenerateOcdProfileModal';
import { OcdCycleStepsCard } from './components/OcdCycleStepsCard';
import { TriggerLogsList } from './components/TriggerLogsList';
import { TriggerLogOptionsList } from './components/TriggerLogOptionsList';
import { UsageTab } from './components/UsageTab';
import { UserProfileTab } from './components/UserProfileTab';
import { ocdProfileService } from './services/ocdProfileService';
import type { OcdInfo } from './services/ocdProfileTypes';
import { triggerLogService } from './services/triggerLogService';
import type { TriggerLog } from './services/triggerLogTypes';
import { triggerLogOptionService } from './services/triggerLogOptionService';
import type { TriggerLogOption } from './services/triggerLogOptionTypes';
import { userProfileService } from './services/userProfileService';
import type { OcdProfileTabType } from './types/tabs';

const tabs: TabConfig<OcdProfileTabType>[] = [
    { id: 'user-profile', label: 'User Profile', icon: '👤' },
    { id: 'usage', label: 'Usage', icon: '📊' },
    { id: 'profile', label: 'OCD Profile', icon: '🧠' },
    { id: 'trigger-logs', label: 'Trigger Logs', icon: '📋' },
    { id: 'quick-options', label: 'Quick Options', icon: '⚡' }
];

const isValidTab = (tab: string | null): tab is OcdProfileTabType => tabs.some(({ id }) => id === tab);

export const OcdProfile = () => {
    const { user } = useAuth();
    const { userId } = useParams<{ userId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<OcdProfileTabType>('user-profile');
    const [ocdInfos, setOcdInfos] = useState<OcdInfo[]>([]);
    const [triggerLogs, setTriggerLogs] = useState<TriggerLog[]>([]);
    const [options, setOptions] = useState<TriggerLogOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingTriggerLogs, setIsLoadingTriggerLogs] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [currentUser, setCurrentUser] = useState<ObsessLessUser | User | null>(null);
    const [showGenerateModal, setShowGenerateModal] = useState(false);

    const isSameUser = useMemo(() => user?.uid === currentUser?.uid, [user, currentUser]);


    useEffect(() => {
        const loadUserProfile = async () => {
            // If userId is provided in URL, load that user's profile
            // Otherwise, load the current logged-in user's profile
            const targetUserId = userId || user?.uid;

            if (targetUserId) {
                setIsLoading(true);
                try {
                    const userProfile = await userProfileService.getUserProfile(targetUserId);
                    if (userProfile) {
                        setCurrentUser(userProfile);
                    } else {
                        console.error(`User with ID "${targetUserId}" not found`);
                        // Fallback to current user
                        if (user) {
                            setCurrentUser(user);
                        }
                    }
                } catch (err) {
                    console.error(err instanceof Error ? err.message : 'Failed to load user profile');
                    // Fallback to current user
                    if (user) {
                        setCurrentUser(user);
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        };
        loadUserProfile();
    }, [user, userId]);

    // Initialize tab from URL on mount
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (isValidTab(tabParam)) {
            setActiveTab(tabParam);
        } else if (tabParam) {
            // Invalid tab in URL, reset to user-profile
            setSearchParams({ tab: 'user-profile' }, { replace: true });
        } else {
            // No tab in URL, set default
            setSearchParams({ tab: 'user-profile' }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (currentUser) {
            const fetchData = async () => {
                setIsLoading(true);
                try {
                    const [ocdInfosData, triggerLogsData, optionsData] = await Promise.all([
                        ocdProfileService.getOcdInfos(currentUser.uid),
                        (async () => {
                            setIsLoadingTriggerLogs(true);
                            try {
                                return await triggerLogService.getTriggerLogs(currentUser.uid);
                            } finally {
                                setIsLoadingTriggerLogs(false);
                            }
                        })(),
                        (async () => {
                            setIsLoadingOptions(true);
                            try {
                                return await triggerLogOptionService.getAllOptions(currentUser.uid);
                            } finally {
                                setIsLoadingOptions(false);
                            }
                        })()
                    ]);
                    setOcdInfos(ocdInfosData);
                    setTriggerLogs(triggerLogsData);
                    setOptions(optionsData);
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
                setIsLoading(false);
            };
            fetchData();
        }
    }, [currentUser]);

    const handleSearchByUserId = async (returnedUser: ObsessLessUser | null) => {
        if (!returnedUser) {
            return;
        }
        setCurrentUser(returnedUser);
    };

    if (isLoading || !currentUser) {
        return <LoadingSpinner />;
    }

    const handleDelete = async (ocdInfo: OcdInfo) => {
        await ocdProfileService.deleteOcdInfo(currentUser.uid, ocdInfo.id);
        setOcdInfos((prev) => prev.filter((info) => info.id !== ocdInfo.id));
    }

    const handleGenerateOcdProfile = async (ocdThemes: string[], questJourneyDay: number, extraGuidance: string) => {
        await ocdProfileService.generateOcdProfile(ocdThemes, questJourneyDay, extraGuidance);
        // Refresh the OCD profile data
        const updatedOcdInfos = await ocdProfileService.getOcdInfos(currentUser!.uid);
        setOcdInfos(updatedOcdInfos);
    }

    const handleTabChange = (tab: OcdProfileTabType) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    const handleClearTriggerLogs = async () => {
        await triggerLogService.deleteAllTriggerLogs(currentUser!.uid);
        setTriggerLogs([]);
    };

    const handleClearOptions = async () => {
        await triggerLogOptionService.deleteAllOptions(currentUser!.uid);
        setOptions([]);
    };

    const handleGenerateOptions = async () => {
        await triggerLogOptionService.generateTriggerLogOptions();
        // Refresh the options data
        const updatedOptions = await triggerLogOptionService.getAllOptions(currentUser!.uid);
        setOptions(updatedOptions);
    };

    const handleUserUpdate = async () => {
        if (currentUser) {
            const updatedProfile = await userProfileService.getUserProfile(currentUser.uid);
            if (updatedProfile) {
                setCurrentUser(updatedProfile);
            }
        }
    };

    const handleSwitchBackToMyProfile = async () => {
        if (user) {
            setIsLoading(true);
            try {
                const userProfile = await userProfileService.getUserProfile(user.uid);
                if (userProfile) {
                    setCurrentUser(userProfile);
                } else {
                    setCurrentUser(user);
                }
            } catch (err) {
                console.error(err instanceof Error ? err.message : 'Failed to load user profile');
                setCurrentUser(user);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className={`p-4 max-w-screen-xl mx-auto ${isSameUser ? 'bg-green-50' : 'bg-red-500'}`}>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">User Profile</h1>
            <UserIdSearch onSearchResult={handleSearchByUserId} />
            <div className="text-sm text-gray-800 mb-4">
                {!isSameUser &&
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
                        <h2 className="text-xl font-bold text-black mb-2">You are editing another user's data!!!</h2>
                        <Button onClick={handleSwitchBackToMyProfile}>Switch back to my profile</Button>
                    </div>
                }
                <p className="mb-2 flex flex-wrap items-center gap-2">Current User ID: <span className="font-bold break-all">{currentUser?.uid}</span> <UserLinks userId={currentUser?.uid || ''} /></p>
                <p className="mb-2">Current User Name: <span className="font-bold">{currentUser?.displayName}</span></p>
            </div>

            {/* Tab Navigation */}
            <TabNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />

            {/* User Profile Tab */}
            {activeTab === 'user-profile' && (
                <UserProfileTab 
                    currentUser={currentUser as ObsessLessUser} 
                    onUserUpdate={handleUserUpdate}
                />
            )}

            {/* Usage Tab */}
            {activeTab === 'usage' && (
                <UsageTab
                    currentUser={currentUser as ObsessLessUser}
                    onUserUpdate={handleUserUpdate}
                />
            )}

            {/* OCD Profile Tab */}
            {activeTab === 'profile' && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2 mb-2">
                        <Button icon={<Copy className="w-4 h-4" />} onClick={() => { navigator.clipboard.writeText(JSON.stringify(ocdInfos)) }}>Copy OCD Profile JSON</Button>
                        <Button icon={<Sparkles className="w-4 h-4" />} onClick={() => setShowGenerateModal(true)} variant="primary">Generate OCD Profile</Button>
                    </div>
                    <OcdCycleStepsCard userId={currentUser.uid} />
                    {ocdInfos.map((ocdInfo) => (
                        <DisplayOcdInfo key={ocdInfo.id} ocdInfo={ocdInfo} onDelete={() => handleDelete(ocdInfo)} />
                    ))}
                </div>
            )}

            {/* Trigger Logs Tab */}
            {activeTab === 'trigger-logs' && (
                <TriggerLogsList
                    triggerLogs={triggerLogs}
                    isLoading={isLoadingTriggerLogs}
                    isCurrentUser={isSameUser}
                    onClearAll={handleClearTriggerLogs}
                />
            )}

            {/* Quick Options Tab */}
            {activeTab === 'quick-options' && (
                <TriggerLogOptionsList
                    options={options}
                    isLoading={isLoadingOptions}
                    isCurrentUser={isSameUser}
                    onClearAll={handleClearOptions}
                    onGenerate={handleGenerateOptions}
                />
            )}

            {showGenerateModal && (
                <GenerateOcdProfileModal
                    onGenerate={handleGenerateOcdProfile}
                    onClose={() => setShowGenerateModal(false)}
                />
            )}
        </div>
    );
};